import { cn } from '@shared/lib/cn'
import { AppHeader } from '@shared/ui/app-header'
import { GlassCard } from '@shared/ui/glass-card'
import { motion } from 'motion/react'
import type { ReactNode } from 'react'

const EASE = [0.25, 0.1, 0.25, 1.0] as const

interface HeroLayoutProps {
  readonly characterImage: string
  /** Current step (1-based). When provided, renders a progress bar below the header. */
  readonly progressStep?: number
  /** Total number of steps. Defaults to 5. */
  readonly progressTotal?: number
  readonly title: string
  readonly subtitle?: string
  readonly speechBubble?: string
  readonly children: ReactNode
  /** Sticky bottom footer (e.g. CTA button). Rendered inside a sticky bar at the bottom. */
  readonly footer?: ReactNode
  readonly className?: string
  readonly gradientStop?: string
  readonly onBack?: () => void
}

export function HeroLayout({
  characterImage,
  progressStep,
  progressTotal = 5,
  title,
  subtitle,
  speechBubble,
  children,
  footer,
  className,
  gradientStop = '65%',
  onBack,
}: HeroLayoutProps) {
  return (
    <div className={cn('relative min-h-dvh @container', className)}>
      {/* Fullscreen character background — 항상 즉시 표시, 애니메이션 없음 */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          maskImage:
            'linear-gradient(to top, transparent 0%, black 20%, black 65%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to top, transparent 0%, black 20%, black 65%, transparent 100%)',
        }}
      >
        <img
          src={characterImage}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-top"
        />
        {/* L1: Dimming overlay */}
        <div className="absolute inset-0" style={{ backgroundColor: 'var(--hero-overlay)' }} />
      </div>

      {/* L2: Top veil gradient */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[40%]"
        style={{
          background:
            'linear-gradient(to bottom, var(--hero-veil-from) 0%, var(--hero-veil-to) 100%)',
        }}
      />

      {/* Scrollable content layer */}
      <div className="relative z-[1] flex min-h-dvh flex-col">
        {/* Header — sticky top */}
        {(onBack || progressStep !== undefined) && (
          <AppHeader leading={<AppHeader.Back onBack={onBack} />} variant="transparent">
            {progressStep !== undefined && (
              <div className="h-0.5 overflow-hidden bg-white/[0.08]">
                <motion.div
                  className="h-full w-full origin-left rounded-full"
                  style={{
                    background: 'linear-gradient(to right, #2E58B8, #5B8AE6)',
                    boxShadow: '0 0 8px #2E58B855',
                  }}
                  initial={{ x: '-100%' }}
                  animate={{ x: `${(progressStep / progressTotal - 1) * 100}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              </div>
            )}
          </AppHeader>
        )}

        {/* Top Text Section */}
        <div
          className={cn(
            'relative z-[2] px-page-x pb-12 text-center',
            onBack || progressStep !== undefined ? 'pt-6' : 'pt-20',
          )}
        >
          <motion.h1
            className="relative z-[1] whitespace-pre-line typo-hero font-extrabold text-foreground"
            style={{ textShadow: 'var(--hero-text-shadow)' }}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05, ease: EASE }}
          >
            {title}
          </motion.h1>
          {subtitle && (
            <motion.p
              className="relative z-[1] mt-1 typo-label text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.12 }}
            >
              {subtitle}
            </motion.p>
          )}
          {speechBubble && (
            <motion.div
              className="relative z-[1] mx-auto mt-3 w-fit"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.15, ease: EASE }}
            >
              <GlassCard
                strong
                className="whitespace-pre-line px-4 py-3 typo-label text-foreground"
                style={{ borderRadius: '16px 16px 4px 16px' }}
              >
                {speechBubble}
              </GlassCard>
            </motion.div>
          )}
        </div>

        {/* Spacer to push form down */}
        <div className="flex-1" />

        {/* Bottom Form Area — gradient fade + 콘텐츠 fade */}
        <motion.div
          className="relative z-[2] px-page-x pb-form-bottom"
          style={{
            paddingTop: 40,
            background: `linear-gradient(to top, var(--hero-base-solid) 0%, var(--hero-base-mid) ${gradientStop}, var(--hero-base-fade) 80%, var(--hero-base-clear) 100%)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, ease: EASE }}
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: EASE }}
          >
            {children}
            {footer && <div className="mt-5">{footer}</div>}
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
